/**
 * StateView — REQ-090 state renderer for admin modules (ODE-P01-T10).
 *
 * Renders one of the six module states (pending/loading/empty/unauthorized/
 * blocked/error) with accessibility semantics: `role="status"` for benign
 * updates, `role="alert"` for failures, `aria-busy` while loading. Each state
 * carries a stable `data-testid="state-<kind>"` for deterministic tests.
 *
 * Traces to: ODE-PRD-REQ-090.
 */

import type { ModuleState } from '../modules/state.js';

export interface StateViewProps {
  readonly state: ModuleState;
  readonly onRetry?: () => void;
}

interface StateCopy {
  readonly title: string;
  readonly detail: string;
}

const COPY: Record<ModuleState['kind'], StateCopy> = {
  pending: {
    title: 'Menunggu',
    detail: 'Modul belum diaktifkan — data belum dihubungkan (ditunda ke tugas berikutnya).',
  },
  loading: {
    title: 'Memuat…',
    detail: 'Memuat data modul.',
  },
  empty: {
    title: 'Belum ada data',
    detail: 'Modul siap; belum ada data untuk ditampilkan.',
  },
  unauthorized: {
    title: 'Tidak diizinkan',
    detail: 'Sesi tidak valid atau kedaluwarsa. Silakan masuk kembali.',
  },
  blocked: {
    title: 'Diblokir',
    detail: 'Akses ditolak: peran tidak memiliki izin atau kode tujuan tidak diberikan.',
  },
  error: {
    title: 'Terjadi kesalahan',
    detail: 'Layanan tidak tersedia atau terjadi kegagalan jaringan.',
  },
};

export function StateView({ state, onRetry }: StateViewProps): JSX.Element {
  const copy = COPY[state.kind];
  const isFailure = state.kind === 'blocked' || state.kind === 'error' || state.kind === 'unauthorized';
  const detail =
    state.kind === 'blocked' || state.kind === 'error' ? state.message : copy.detail;

  return (
    <section
      data-testid={`state-${state.kind}`}
      role={isFailure ? 'alert' : 'status'}
      aria-live="polite"
      aria-busy={state.kind === 'loading'}
      className={`state state-${state.kind}`}
    >
      <h3>{copy.title}</h3>
      <p>{detail}</p>
      {(state.kind === 'error' || state.kind === 'blocked') && onRetry !== undefined && (
        <button type="button" onClick={onRetry}>
          Coba lagi
        </button>
      )}
    </section>
  );
}